import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { autocomplete, AutocompletePlugin } from '@algolia/autocomplete-js';

import { ProductHit } from '@src/utils/algolia';

export interface TypeAlgoliaProps {
  className?: string;
  onChange: (text: string) => void;
  placeholder?: string;
  suggestionPlugin: AutocompletePlugin<ProductHit, any>;
  value: string;
}

/**
 * @name TypeAlgolia
 * @description An input component that uses Algolia's autocomplete. The heavy
 * lifting of how to use the indexes and items should be done using a plugin and
 * then passed to this component.
 */
export const TypeAlgolia = (props: TypeAlgoliaProps) => {
  const { className, onChange, placeholder, suggestionPlugin, value } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const panelRootRef = React.useRef<any>(null);
  const rootRef = React.useRef<any>(null);
  const autoCompleteRef = React.useRef<any>(null);

  const onFocusOut = () => {
    if (autoCompleteRef.current) {
      autoCompleteRef.current.setIsOpen(false);
    }
  };

  /**
   * Need to have this typed as any because the the type needed for
   * addEventListener has a 'this' and we can't have 'this' in an arrow
   * function.
   */
  const onInput = (event: any) => {
    onChange(event.target.value);
  };

  React.useEffect(() => {
    if (!containerRef.current) return;

    autoCompleteRef.current = autocomplete({
      container: containerRef.current,
      // Prevents the mobile full screen container that is the default.
      detachedMediaQuery: 'none',
      placeholder,
      plugins: [suggestionPlugin],
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;

          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }

        panelRootRef.current.render(children);
      },
      renderer: {
        Fragment: React.Fragment,
        createElement: React.createElement,

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        render: () => {}
      }
    });

    const inputBox = containerRef.current.getElementsByTagName('input')[0];
    if (inputBox) {
      inputBox.addEventListener('focusout', onFocusOut);
      /**
       * We are using the onInput event because the onStateChange event of the
       * autocomplete plugin fires on focus changes too which messes up logic
       * if we are listening to both onSelect and onChange events.
       */
      inputBox.addEventListener('input', onInput);
    }

    return () => {
      autoCompleteRef.current.destroy();

      if (inputBox) {
        inputBox.removeEventListener('focusout', onFocusOut);
        inputBox.removeEventListener('input', onInput);
      }
    };
  }, []);

  /**
   * Turns this component into more of a controlled component. This is needed
   * since there is now really good way to get/set the value of the input.
   */
  React.useEffect(() => {
    if (autoCompleteRef.current) {
      autoCompleteRef.current.setQuery(value);
    }
  }, [value]);

  return <div className={className} ref={containerRef} />;
};
